import merge from 'deepmerge'
import equal from 'deep-equal'

import SEARCH from '@/web/store/types/search'
import REQUEST from '@/web/store/registered/request'
import { storage } from '@/tools/storage'
import { pushEvent } from '@/tools/data-layer/data-layer'

import {
  STORES_SEARCH_CONTEXT,
  PRODUCTS_SEARCH_CONTEXT,
  getProductSearchContexByRef,
  getStoreSearchContexByRef
} from '@/tools/constants/contexts-for-metrics'

import { getNormalizedParamsFromUrl, setHistoryPushState, getFiltersApplied, removeFiltersAppliedNotAllowed, getStaticOptions } from './search/url-helpers'
import { AUTO_SELECTED_OPTIONS } from '@/web/components/product-feed-filters/constants'
import { PRODUCT_SEARCH_CONTEXT, IN_USER_STORE_CONTEXTS } from '@/web/components/header-search/context-selector/constants'

import {
  UPDATE_TRIGGERS,
  PREVIOUS_PAGES_CURSORS_LIMIT,
  AUTO_LOAD_SEARCH_ID,
  RECENTLY_SEARCHED_TERMS_STORAGE_KEY,
  MAX_RECENTLY_SEARCHED_TERMS
} from '@/web/components/product-feed/constants'

const state = {
  term: '',
  inputTerm: '',
  recentlySeachedTerms: [],
  shouldPreventRedirect: false,
  aggregations: {},
  appliedOptions: {},
  staticOptions: {},
  updateTriggers: null,
  department: '',
  sellerForSearch: {},
  isFetchingProducts: false,
  isFetchingAggregations: false,
  currentPage: 1,
  pagesCount: 1,
  totalResults: null,
  currentPageCursor: '',
  nextPageCursor: '',
  previousPagesCursors: [],
  searchId: '',
  searchContext: '',
  selectedContext: '',
  isHighlightsPanelVisible: false
}

const getters = {
  [SEARCH.GETTERS.GET_TERM]: state => {
    return state.term
  },
  [SEARCH.GETTERS.GET_INPUT_TERM]: state => {
    return state.inputTerm
  },
  [SEARCH.GETTERS.GET_RECENTLY_SEARCHED_TERMS]: state => {
    return state.recentlySeachedTerms
  },
  [SEARCH.GETTERS.GET_SHOULD_PREVENT_REDIRECT]: state => {
    return state.shouldPreventRedirect
  },
  [SEARCH.GETTERS.GET_AGGREGATIONS]: state => {
    return state.aggregations
  },
  [SEARCH.GETTERS.GET_APPLIED_OPTIONS]: state => {
    return state.appliedOptions
  },
  [SEARCH.GETTERS.GET_CURRENT_PAGE_CURSOR]: state => {
    return state.currentPageCursor
  },
  [SEARCH.GETTERS.GET_UPDATE_TRIGGERS]: state => {
    return state.updateTriggers
  },
  [SEARCH.GETTERS.GET_STATIC_OPTIONS]: state => {
    return state.staticOptions
  },
  [SEARCH.GETTERS.GET_DEPARTMENT]: state => {
    return state.department
  },
  [SEARCH.GETTERS.GET_TOTAL_RESULTS]: state => {
    return state.totalResults
  },
  [SEARCH.GETTERS.GET_SELLER_FOR_SEARCH]: state => {
    return state.sellerForSearch
  },
  [SEARCH.GETTERS.GET_IS_FETCHING_PRODUCTS]: state => {
    return state.isFetchingProducts
  },
  [SEARCH.GETTERS.GET_IS_FETCHING_AGGREGATIONS]: state => {
    return state.isFetchingAggregations
  },
  [SEARCH.GETTERS.GET_PAGES_COUNT]: state => {
    return state.pagesCount
  },
  [SEARCH.GETTERS.GET_CURRENT_PAGE]: state => {
    return state.currentPage
  },
  [SEARCH.GETTERS.GET_NEXT_PAGE_CURSOR]: state => {
    return state.nextPageCursor
  },
  [SEARCH.GETTERS.GET_PREVIOUS_PAGES_CURSORS]: state => {
    return state.previousPagesCursors
  },
  [SEARCH.GETTERS.GET_SEARCH_ID]: state => {
    return state.searchId
  },
  [SEARCH.GETTERS.GET_SEARCH_CONTEXT]: state => {
    return state.searchContext
  },
  [SEARCH.GETTERS.GET_IS_STORE]: state => {
    return !!state.sellerForSearch.seller_id
  },
  [SEARCH.GETTERS.GET_SELECTED_CONTEXT]: state => {
    return state.selectedContext
  },
  [SEARCH.GETTERS.GET_IS_PRODUCT_CONTEXT]: state => {
    return [PRODUCT_SEARCH_CONTEXT, IN_USER_STORE_CONTEXTS.all].includes(state.selectedContext)
  },
  [SEARCH.GETTERS.GET_IS_HIGHLIGHTS_PANEL_VISIBLE]: state => {
    return state.isHighlightsPanelVisible
  }
}

const mutations = {
  [SEARCH.MUTATIONS.SET_TERM]: (state, payload) => {
    state.term = payload
  },
  [SEARCH.MUTATIONS.SET_INPUT_TERM]: (state, payload) => {
    state.inputTerm = payload
  },
  [SEARCH.MUTATIONS.SET_RECENTLY_SEARCHED_TERM]: (state, payload) => {
    if (!payload || !!state.sellerForSearch.seller_id) return

    let recentlySeachedTerms = storage.local.get(RECENTLY_SEARCHED_TERMS_STORAGE_KEY) || []

    if (recentlySeachedTerms.length) {
      recentlySeachedTerms = recentlySeachedTerms.filter(term => term !== payload)
      recentlySeachedTerms.unshift(payload)
      recentlySeachedTerms.length > MAX_RECENTLY_SEARCHED_TERMS && recentlySeachedTerms.pop()
    } else {
      recentlySeachedTerms.push(payload)
    }

    state.recentlySeachedTerms = recentlySeachedTerms
    storage.local.set(RECENTLY_SEARCHED_TERMS_STORAGE_KEY, recentlySeachedTerms)
  },
  [SEARCH.MUTATIONS.SET_SHOULD_PREVENT_REDIRECT]: (state, payload) => {
    state.shouldPreventRedirect = payload
  },
  [SEARCH.MUTATIONS.SET_AGGREGATIONS]: (state, payload) => {
    state.aggregations = payload
  },
  [SEARCH.MUTATIONS.SET_APPLIED_OPTIONS]: (state, payload) => {
    state.appliedOptions = merge({}, payload)
  },
  [SEARCH.MUTATIONS.SET_SEARCH_ID]: (state, payload) => {
    state.searchId = payload
  },
  [SEARCH.MUTATIONS.SET_SEARCH_CONTEXT]: (state, payload) => {
    state.searchContext = payload
  },
  [SEARCH.MUTATIONS.SET_CURRENT_PAGE_CURSOR]: (state, payload) => {
    state.currentPageCursor = payload
  },
  [SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS]: (state, payload) => {
    state.updateTriggers = payload
  },
  [SEARCH.MUTATIONS.SET_STATIC_OPTIONS]: (state, payload) => {
    state.staticOptions = payload
  },
  [SEARCH.MUTATIONS.SET_DEPARTMENT]: (state, payload) => {
    state.department = payload
  },
  [SEARCH.MUTATIONS.SET_TOTAL_RESULTS]: (state, payload) => {
    state.totalResults = payload
  },
  [SEARCH.MUTATIONS.SET_SELLER_FOR_SEARCH]: (state, payload) => {
    state.sellerForSearch = Object.assign({}, payload)
  },
  [SEARCH.MUTATIONS.SET_IS_FETCHING_PRODUCTS]: (state, payload) => {
    state.isFetchingProducts = payload
  },
  [SEARCH.MUTATIONS.SET_IS_FETCHING_AGGREGATIONS]: (state, payload) => {
    state.isFetchingAggregations = payload
  },
  [SEARCH.MUTATIONS.SET_PAGES_COUNT]: (state, payload) => {
    state.pagesCount = payload
  },
  [SEARCH.MUTATIONS.SET_CURRENT_PAGE]: (state, payload) => {
    state.currentPage = payload || 1
  },
  [SEARCH.MUTATIONS.SET_NEXT_PAGE_CURSOR]: (state, payload) => {
    state.nextPageCursor = payload
  },
  [SEARCH.MUTATIONS.SET_PREVIOUS_PAGES_CURSORS]: (state, payload) => {
    payload = payload || []

    if (payload.length > PREVIOUS_PAGES_CURSORS_LIMIT) {
      payload = payload.slice(-PREVIOUS_PAGES_CURSORS_LIMIT)
    }

    state.previousPagesCursors = payload
  },
  [SEARCH.MUTATIONS.SET_PAGINATION_WITH_URL_PARAMS]: (state, payload) => {
    const currentPageCursor = payload.current_page_cursor || ''

    const previousPagesCursors = Array.isArray(payload.previous_pages_cursors) ?
      payload.previous_pages_cursors :
      [payload.previous_pages_cursors].filter(v => typeof v !== 'undefined')

    let currentPage

    if (previousPagesCursors.length) {
      currentPage = previousPagesCursors.length + 2
    } else {
      currentPage = currentPageCursor ? 2 : 1
    }

    state.currentPageCursor = currentPageCursor
    state.currentPage = currentPage
    state.previousPagesCursors = previousPagesCursors
  },
  [SEARCH.MUTATIONS.RESET_PAGINATION]: (state) => {
    state.currentPageCursor = ''
    state.currentPage = 1
    state.previousPagesCursors = []
  },
  [SEARCH.MUTATIONS.SET_SELECTED_CONTEXT]: (state, payload) => {
    state.selectedContext = payload
  },
  [SEARCH.MUTATIONS.SET_IS_HIGHLIGHTS_PANEL_VISIBLE]: (state, payload) => {
    state.isHighlightsPanelVisible = payload
  }
}

const actions = {
  [SEARCH.ACTIONS.INITIAL_LOAD]: ({ commit, getters, state, dispatch }, payload) => {
    dispatch(REQUEST.ACTIONS.SETUP_ROUTE, {
      spec: payload.routeSpec,
      abbrParamsMap: payload.abbrParamsMap,
      parserOptions: {
        path: { allowGroupedValues: true },
        query: { arrayFormat: 'repeat' }
      }
    }).then(() => {
      const filterTypes = payload.allowedFilters
      const paramsFromUrl = getNormalizedParamsFromUrl()
      const term = paramsFromUrl.query || ''
      const department = paramsFromUrl.department || ''
      const searchId = paramsFromUrl.search_id || AUTO_LOAD_SEARCH_ID.get_uuid()
      const appliedOptions = removeFiltersAppliedNotAllowed(getFiltersApplied(paramsFromUrl), filterTypes)
      const staticOptions = getStaticOptions(paramsFromUrl)
      const searchContext = getters['SEARCH/GET_IS_STORE'] ?
        getStoreSearchContexByRef(paramsFromUrl.ref) :
        getProductSearchContexByRef(paramsFromUrl.ref)

      if (term) {
        pushEvent('search', { search_term: term })
      }

      commit(SEARCH.MUTATIONS.SET_TERM, term)
      commit(SEARCH.MUTATIONS.SET_INPUT_TERM, term)
      commit(SEARCH.MUTATIONS.SET_RECENTLY_SEARCHED_TERM, term)
      commit(SEARCH.MUTATIONS.SET_PAGINATION_WITH_URL_PARAMS, paramsFromUrl)
      commit(SEARCH.MUTATIONS.SET_APPLIED_OPTIONS, appliedOptions)
      commit(SEARCH.MUTATIONS.SET_SEARCH_ID, searchId)
      commit(SEARCH.MUTATIONS.SET_SEARCH_CONTEXT, searchContext)
      commit(SEARCH.MUTATIONS.SET_DEPARTMENT, department)
      commit(SEARCH.MUTATIONS.SET_STATIC_OPTIONS, staticOptions)
      commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, UPDATE_TRIGGERS.INITIAL_LOAD)

      setHistoryPushState({
        term,
        department,
        appliedOptions,
        currentPageCursor: state.currentPageCursor,
        staticOptions: state.staticOptions,
        searchId: state.searchId,
        disponiveis: paramsFromUrl.disponiveis,
        previousPagesCursors: state.previousPagesCursors,
        method: 'replace'
      })
    })
  },
  [SEARCH.ACTIONS.CHANGE_TERM]: ({ commit, getters, state }, payload) => {
    const shippingRange = payload.shouldSetShippingRange &&
      AUTO_SELECTED_OPTIONS.shipping_range()
    const searchId = AUTO_LOAD_SEARCH_ID.get_uuid()
    const searchContext = getters['SEARCH/GET_IS_STORE'] ?
        STORES_SEARCH_CONTEXT.PRODUCTS_SEARCH_STORE_PAGE :
        PRODUCTS_SEARCH_CONTEXT.PRODUCTS_SEARCH

    pushEvent('search', { search_term: payload.term })

    commit(SEARCH.MUTATIONS.SET_TERM, payload.term)
    commit(SEARCH.MUTATIONS.SET_RECENTLY_SEARCHED_TERM, payload.term)
    commit(SEARCH.MUTATIONS.SET_APPLIED_OPTIONS, shippingRange ? { shipping_range: shippingRange } : {})
    commit(SEARCH.MUTATIONS.SET_SEARCH_ID, searchId)
    commit(SEARCH.MUTATIONS.RESET_PAGINATION)
    commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, UPDATE_TRIGGERS.CHANGE_TERM)
    commit(SEARCH.MUTATIONS.SET_SEARCH_CONTEXT, searchContext)

    if (Object.keys(state.staticOptions).length) {
      commit(SEARCH.MUTATIONS.SET_STATIC_OPTIONS, {})
    }

    if (payload.term.length === 0) {
      commit(SEARCH.MUTATIONS.SET_DEPARTMENT, '')
    }

    setHistoryPushState({
      term: state.term,
      department: state.department,
      appliedOptions: state.appliedOptions,
      currentPageCursor: state.currentPageCursor,
      staticOptions: state.staticOptions,
      searchId: state.searchId
    })
  },
  [SEARCH.ACTIONS.PAGINATE]: ({ commit, state }, payload) => {
    let currentPage
    let currentPageCursor
    let previousPagesCursors = state.previousPagesCursors.slice()

    switch (payload) {
      case 'backward':
        currentPageCursor = previousPagesCursors.pop() || ''
        currentPage = currentPageCursor ? (state.currentPage - 1) : 1
        break
      case 'forward':
        if (state.currentPageCursor) {
          previousPagesCursors.push(state.currentPageCursor)
        }

        currentPageCursor = state.nextPageCursor
        currentPage = state.currentPage + 1
    }

    commit(SEARCH.MUTATIONS.SET_PREVIOUS_PAGES_CURSORS, previousPagesCursors)
    commit(SEARCH.MUTATIONS.SET_CURRENT_PAGE, currentPage)
    commit(SEARCH.MUTATIONS.SET_CURRENT_PAGE_CURSOR, currentPageCursor)
    commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, UPDATE_TRIGGERS.CHANGE_PAGINATION)

    setHistoryPushState({
      term: state.term,
      department: state.department,
      appliedOptions: state.appliedOptions,
      searchId: state.searchId,
      currentPageCursor: state.currentPageCursor,
      previousPagesCursors: state.previousPagesCursors,
      staticOptions: state.staticOptions
    })
  },
  [SEARCH.ACTIONS.CHANGE_FILTER]: ({ commit, getters, state }, payload) => {
    const changedFilters = Object.keys(Object.keys(payload).reduce((changedOptions, key) => {
      if (!equal(payload[key], state.appliedOptions[key], { strict: true })) {
        changedOptions[key] = payload[key]
      }

      return changedOptions
    }, {}))

    const hasOnlyChangedSort = changedFilters.length === 1 && changedFilters[0] === 'sort'
    const searchId = AUTO_LOAD_SEARCH_ID.get_uuid()
    const searchContext = getters['SEARCH/GET_IS_STORE'] ?
        STORES_SEARCH_CONTEXT.PRODUCTS_SEARCH_STORE_PAGE :
        PRODUCTS_SEARCH_CONTEXT.PRODUCTS_SEARCH

    commit(SEARCH.MUTATIONS.SET_APPLIED_OPTIONS, payload)
    commit(SEARCH.MUTATIONS.SET_SEARCH_ID, searchId)
    commit(SEARCH.MUTATIONS.RESET_PAGINATION)
    commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, hasOnlyChangedSort ? UPDATE_TRIGGERS.CHANGE_SORT : UPDATE_TRIGGERS.CHANGE_FILTERS)
    commit(SEARCH.MUTATIONS.SET_SEARCH_CONTEXT, searchContext)

    setHistoryPushState({
      term: state.term,
      department: state.department,
      appliedOptions: state.appliedOptions,
      searchId: state.searchId,
      currentPageCursor: state.currentPageCursor,
      staticOptions: state.staticOptions
    })
  },
  [SEARCH.ACTIONS.NAVIGATE_HISTORY]: ({ commit, getters, dispatch }, payload) => {
    dispatch(REQUEST.ACTIONS.PARSE_PARAMS).then(() => {
      const paramsFromUrl = getNormalizedParamsFromUrl()
      const staticOptions = getStaticOptions(paramsFromUrl)
      const searchContext = getters['SEARCH/GET_IS_STORE'] ?
        getStoreSearchContexByRef(paramsFromUrl.ref) :
        getProductSearchContexByRef(paramsFromUrl.ref)

      commit(SEARCH.MUTATIONS.SET_TERM, paramsFromUrl.query || '')
      commit(SEARCH.MUTATIONS.SET_APPLIED_OPTIONS, getFiltersApplied(paramsFromUrl))
      commit(SEARCH.MUTATIONS.SET_DEPARTMENT, paramsFromUrl.department || '')
      commit(SEARCH.MUTATIONS.SET_SEARCH_ID, paramsFromUrl.search_id || AUTO_LOAD_SEARCH_ID.get_uuid())
      commit(SEARCH.MUTATIONS.SET_STATIC_OPTIONS, staticOptions)
      commit(SEARCH.MUTATIONS.SET_PAGINATION_WITH_URL_PARAMS, paramsFromUrl)
      commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, UPDATE_TRIGGERS.NAVIGATE_HISTORY)
      commit(SEARCH.MUTATIONS.SET_SEARCH_CONTEXT, searchContext)
    })
  },
  [SEARCH.ACTIONS.CHANGE_DEPARTMENT]: ({ commit, state }, payload) => {
    const appliedOptions = Object.assign({}, state.appliedOptions)
    delete appliedOptions.sub_categories
    delete appliedOptions.size_types
    const searchId = AUTO_LOAD_SEARCH_ID.get_uuid()

    commit(SEARCH.MUTATIONS.SET_DEPARTMENT, payload)
    commit(SEARCH.MUTATIONS.SET_APPLIED_OPTIONS, Object.assign({}, appliedOptions))
    commit(SEARCH.MUTATIONS.SET_SEARCH_ID, searchId)
    commit(SEARCH.MUTATIONS.RESET_PAGINATION)
    commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, UPDATE_TRIGGERS.CHANGE_DEPARTMENT)

    setHistoryPushState({
      term: state.term,
      department: state.department,
      appliedOptions: state.appliedOptions,
      searchId: state.searchId,
      currentPageCursor: state.currentPageCursor,
      staticOptions: state.staticOptions
    })
  },
  [SEARCH.ACTIONS.CHANGE_STATIC_OPTIONS]: ({ commit }, payload) => {
    commit(SEARCH.MUTATIONS.SET_STATIC_OPTIONS, payload)
    commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, UPDATE_TRIGGERS.CHANGE_STATIC_OPTIONS)

    setHistoryPushState({
      term: state.term,
      department: state.department,
      appliedOptions: state.appliedOptions,
      currentPageCursor: state.currentPageCursor,
      staticOptions: state.staticOptions
    })
  },
  [SEARCH.ACTIONS.RELOAD]: ({ commit }) => {
    commit(SEARCH.MUTATIONS.SET_UPDATE_TRIGGERS, UPDATE_TRIGGERS.RELOAD)
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
